import React, { useState, useMemo, useEffect } from 'react';
import { UserProfile } from '../../types';
import { Trash2, Square, CheckSquare } from 'lucide-react';

interface Props {
    currentUser: UserProfile;
    usersList: UserProfile[];
    onDataNeededRefresh: () => void;
    rankFilter?: number[];
}

interface EditingCell {
    id: number;
    field: string;
}

const UsersTab: React.FC<Props> = ({ currentUser, usersList: initialUsersList, onDataNeededRefresh, rankFilter }) => {
    const filteredUserList = useMemo(() => rankFilter ? initialUsersList.filter(user => rankFilter.includes(user.rank ?? 1)) : initialUsersList, [initialUsersList, rankFilter]);
    const [usersList, setUsersList] = useState(filteredUserList);
    const [selectedIds, setSelectedIds] = useState<Set<number | string>>(new Set());
    const [lastSelectedId, setLastSelectedId] = useState<number | string | null>(null);
    const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
    const [tempCellValue, setTempCellValue] = useState<string>("");

    useEffect(() => {
        setUsersList(filteredUserList);
    }, [filteredUserList]);
    
    const handleSelectAll = () => {
        if (usersList.length > 0 && selectedIds.size === usersList.length) {
            setSelectedIds(new Set());
        } else {
            const allIds = new Set(usersList.map(item => item.id).filter(Boolean) as number[]);
            setSelectedIds(allIds);
        }
    };

    const handleSelectRow = (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const newSelected = new Set(selectedIds);
        
        if (e.shiftKey && lastSelectedId !== null) {
            const currentIndex = usersList.findIndex(u => u.id === id);
            const lastIndex = usersList.findIndex(u => u.id === lastSelectedId);
            const start = Math.min(currentIndex, lastIndex);
            const end = Math.max(currentIndex, lastIndex);
            for (let i = start; i <= end; i++) {
                if(usersList[i].id) newSelected.add(usersList[i].id!);
            }
        } else {
            if (newSelected.has(id)) newSelected.delete(id);
            else newSelected.add(id);
        }

        setSelectedIds(newSelected);
        setLastSelectedId(id);
    };

    const handleBulkDeleteUsers = async () => {
        if (selectedIds.size === 0) return;
        if (!confirm(`Delete ${selectedIds.size} users?`)) return;

        try {
            const res = await fetch('/api/users', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ ids: Array.from(selectedIds) })
            });
            if (res.ok) {
                onDataNeededRefresh();
                setSelectedIds(new Set());
            }
        } catch (e) { console.error(e); }
    };

    const handleDeleteSingleUser = async (id: number) => {
        if (!confirm(`Delete user #${id}?`)) return;
        try {
            await fetch('/api/users', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ ids: [id] })
            });
            onDataNeededRefresh();
        } catch (e) { console.error(e); }
    };

    const handleCellClick = (item: UserProfile, field: string) => {
        if (!item.id || ['id', 'actions', 'avatar'].includes(field)) return;
        setEditingCell({ id: item.id, field });
        setTempCellValue(String(item[field as keyof UserProfile] || ''));
    };

    const handleCellBlur = async () => {
        if (!editingCell) return;
        
        // This is a partial update for just one field. The backend logic supports this.
        const payload = {
            id: editingCell.id,
            [editingCell.field]: tempCellValue
        };
        
        // Optimistic update
        const updatedList = usersList.map(u => u.id === editingCell.id ? { ...u, [editingCell.field]: tempCellValue } : u);
        setUsersList(updatedList);
        
        setEditingCell(null);

        try {
            await fetch('/api/users', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify(payload)
            });
        } catch (e) { 
            console.error("Error saving cell", e); 
            onDataNeededRefresh(); // Revert on error
        }
    };

    const userColumns = [
        { key: 'avatar', label: '', width: '40px' },
        { key: 'id', label: 'ID', width: '60px' }, 
        { key: 'name', label: 'NAME' },
        { key: 'phone', label: 'PHONE' }, 
        { key: 'email', label: 'EMAIL' },
        { key: 'birth', label: 'BIRTH DATE' }, 
        { key: 'last_active', label: 'LAST ACTIVE', width: '200px' },
        { key: 'rank', label: 'RANK', width: '80px' },
        { key: 'actions', label: 'ACTIONS', width: '60px' }
    ];

    return (
        <div className="w-full relative animate-fade-in">
            {selectedIds.size > 0 && (
                <div className="mb-4 flex items-center gap-2 animate-fade-in bg-blue-900/30 border border-blue-700/50 rounded px-2 py-1 self-start">
                    <span className="text-xs text-blue-200">{selectedIds.size} selected</span>
                    <button onClick={handleBulkDeleteUsers} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
            )}
            <div className="overflow-x-auto border border-gray-800 rounded-sm">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-mystic-box">
                        <tr className="border-b border-gray-800 text-gray-500 text-[10px] uppercase font-bold tracking-wider">
                            <th className="p-2 border-r border-gray-800 w-10 text-center">
                                <div onClick={handleSelectAll} className="cursor-pointer flex justify-center text-gray-500 hover:text-white">
                                    {usersList.length > 0 && selectedIds.size === usersList.length ? <CheckSquare className="w-4 h-4 text-blue-400" /> : <Square className="w-4 h-4" />}
                                </div>
                            </th>
                            {userColumns.map(col => <th key={col.key} className="p-2 border-r border-gray-800 last:border-r-0" style={{ width: col.width }}>{col.label}</th>)}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800 bg-mystic-bg">
                        {usersList.map(u => {
                            const isSelected = u.id && selectedIds.has(u.id);
                            return (
                                <tr key={u.id} className={`group hover:bg-mystic-btn ${isSelected ? 'bg-blue-900/10' : ''}`}>
                                    <td className="p-2 border-r border-gray-800 text-center">
                                        {u.id && <div onClick={(e) => u.id && handleSelectRow(u.id, e)} className="cursor-pointer flex justify-center">
                                            {isSelected ? <CheckSquare className="w-4 h-4 text-blue-400" /> : <Square className="w-4 h-4 text-gray-700 group-hover:text-gray-500" />}
                                        </div>}
                                    </td>
                                    {userColumns.map(col => {
                                        const isEditing = editingCell?.id === u.id && editingCell?.field === col.key;
                                        return (
                                            <td key={col.key} className="p-2 border-r border-gray-800 last:border-r-0 text-xs text-gray-300 relative h-9" onClick={() => handleCellClick(u, col.key)}>
                                                {(() => {
                                                    if (col.key === 'actions') {
                                                        return u.id && <div className="text-center"><button onClick={() => u.id && handleDeleteSingleUser(u.id)} className="text-gray-600 hover:text-red-400"><Trash2 className="w-4 h-4" /></button></div>
                                                    }
                                                    if (col.key === 'avatar') {
                                                        return <div className="w-6 h-6 rounded-full bg-cover bg-center bg-gray-700" style={{ backgroundImage: u.avatar ? `url(${u.avatar})` : 'none' }}></div>
                                                    }
                                                    if (isEditing) {
                                                        return <input type="text" value={tempCellValue} onChange={(e) => setTempCellValue(e.target.value)} onBlur={handleCellBlur} autoFocus onKeyDown={e => e.key === 'Enter' && handleCellBlur()} className="absolute inset-0 w-full h-full bg-mystic-inset px-2 text-white outline-none focus:ring-1 focus:ring-mystic-accent/50 text-xs font-mono" />
                                                    }
                                                    return (
                                                        <div className={`w-full h-full flex items-center ${!['id', 'actions', 'avatar'].includes(col.key) ? 'cursor-text' : ''}`}>
                                                            {col.key === 'rank' ? (u.rank === 0 ? 'ADMIN' : u.rank === 2 ? 'EXPERT' : u.rank === 3 ? 'BOT' : 'USER') : u[col.key as keyof UserProfile]}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                        )
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default UsersTab;